import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import adminApi from '@/lib/adminApi';
import { ScrollText, Search, Download, ChevronLeft, ChevronRight, Clock } from 'lucide-react';

const ACTION_OPTIONS = [
  'user_created', 'user_disabled', 'user_enabled', 'user_deleted',
  'password_reset_forced', 'mfa_reset', 'role_changed',
  'organisation_suspended', 'organisation_activated', 'organisation_created',
  'impersonation_start', 'impersonation_end', 'login_success', 'login_failure',
];

export default function AdminAuditLogPage() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.getAuditLog({ page, action: actionFilter, date_from: dateFrom, date_to: dateTo });
      setEntries(data.items);
      setTotal(data.total);
      setPages(data.pages);
    } catch (error) {
      console.error('Failed to load audit log:', error);
    }
    setLoading(false);
  }, [page, actionFilter, dateFrom, dateTo]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const handleExport = () => {
    const url = adminApi.exportAuditLog({ date_from: dateFrom, date_to: dateTo });
    window.open(url, '_blank');
  };

  return (
    <AdminLayout title="Audit Log" breadcrumbs={[{ label: 'Audit Log' }]}>
      <div className="flex items-center gap-3 mb-4" data-testid="admin-audit-toolbar">
        <select data-testid="admin-audit-action-filter" value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(1); }}
          className="border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm text-[#4E6974]">
          <option value="">All actions</option>
          {ACTION_OPTIONS.map(a => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
        </select>
        <input data-testid="admin-audit-date-from" type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
          className="border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm text-[#4E6974]" />
        <input data-testid="admin-audit-date-to" type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
          className="border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm text-[#4E6974]" />
        <div className="flex-1" />
        <button data-testid="admin-audit-export-btn" onClick={handleExport}
          className="flex items-center gap-1.5 px-4 py-2 border border-[#E0E0E0] text-[#4E6974] text-sm rounded-lg hover:bg-[#F5F5F5]">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#E8ECF0] overflow-hidden" data-testid="admin-audit-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E8ECF0] bg-[#F8FAFB]">
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Time</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Action</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Actor</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Target</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">IP</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Metadata</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center py-8 text-[#B0BEC5]">Loading...</td></tr>}
            {!loading && entries.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-[#B0BEC5]">No audit entries</td></tr>}
            {entries.map(e => (
              <tr key={e.id} className="border-b border-[#F5F5F5]">
                <td className="px-4 py-3 text-xs text-[#4E6974] whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 bg-[#F5F5F5] rounded text-xs font-medium text-[#0A2533]">{e.action.replace(/_/g, ' ')}</span>
                </td>
                <td className="px-4 py-3 text-xs text-[#4E6974] font-mono">{(e.actor_user_id || '').slice(0, 8)}...</td>
                <td className="px-4 py-3 text-xs text-[#4E6974]">
                  {e.target_user_id && <span className="font-mono">{e.target_user_id.slice(0, 8)}...</span>}
                  {e.target_organisation_id && <span className="font-mono ml-1">(org: {e.target_organisation_id.slice(0, 8)}...)</span>}
                </td>
                <td className="px-4 py-3 text-xs text-[#B0BEC5] font-mono">{e.ip_address}</td>
                <td className="px-4 py-3 text-xs text-[#B0BEC5] max-w-[200px] truncate">{JSON.stringify(e.metadata)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8ECF0]">
            <span className="text-xs text-[#4E6974]">{total} entries</span>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-1 rounded hover:bg-[#F5F5F5] disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
              <span className="text-xs text-[#4E6974]">{page} / {pages}</span>
              <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="p-1 rounded hover:bg-[#F5F5F5] disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
